/* eslint-disable consistent-return */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from "react";
import { produce } from "immer";

import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import axios from "axios";
import { sortBy } from "lodash";
import api from "@shared/phoneDncStatusApi";
import { snakefyKeys, tryCatchHandlr } from "@shared/Utilities";
import { getPostJsonHeaders } from "@shared/axiosHelpers";

import { getExternalApi } from "@shared/externalApi";
import TextButton from "@shared/v2/TextButton";
import { PopoverTrigger, Popover, PopoverContent, PopoverItem } from "@shared/v2/Popover";

import css from "./person-detail-styles.module.css";
import EditableElementWithAdd from "./EditableElementWithAdd";
import SocialIcon from "./SocialIcon";
import EditSocialMediaModal from "./modals/EditSocialMediaModal";
import Loading from "../../Goals/components/Loader";
import {
  closeEditSocialMediaModal,
  openEditSocialMediaModal,
  updatePersonData,
} from "../actions/personDetailActionCreators";
import CollapsibleElement from "./CollapsibleElement";
import IconButton from "../../shared/v2/IconButton";
import { EllipsisSolidV6, PencilSolidV6, PlusSolidV6, TrashCanSolidV6 } from "../../shared/v2/Icomoon";
import DuplicateLeadCard from "./DuplicateLeadCard";
import { useMessagingServiceUrl } from "../../reducers/messagingReducer/selectors";
import useIsEditable from "../shared/useIsEditable";
import AreaPro from "./AreaPro";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import AddAddressModal from "./AddAddressModal";
import DeleteAddressModal from "./DeleteAddressModal";
import { updatePersonPromise } from "../actions/updatePerson";
import EditAddressModal from "./EditAddressModal";

const STYLES = {
  ellipsisButton: "tw-w-[178px] tw-flex tw-items-center tw-gap-[8px]",
  ellipsisTxt: "tw-text-14d tw-text-gray-75",
}

const getListingStatusColors = (status) => {
  switch (status) {
    case "Active":
      return { text: "tw-text-[#36b25f]", background: "tw-bg-[#EDFAF1]" };
    case "Pending":
    case "Active Under Contract":
      return { text: "tw-text-[#e5941b]", background: "tw-bg-[#faf1e6]" };
    case "Closed":
      return { text: "tw-text-[#e5342e]", background: "tw-bg-[#fae6e6]" };
    case "Withdrawn":
    default:
      return { text: "tw-text-[#666666]", background: "tw-bg-[#f5f7f7]" };
  }
};

const phoneNumbers = (person) =>
  (person.included || [])
    .filter((a) => a.type === "phone_detail" && a.id !== null)
    .map((phone) => phone.attributes?.value);

const getAddressAttributes = (person) =>
  (person.included || [])
    .filter((a) => a.type === "address" && a.id !== null)
    .map((address) => ({ ...address.attributes, id: address.id }));

const getFormattedAddressesHomeFirst = (person) =>
  getAddressAttributes(person)
    .sort((a) => (a.address_type === "home" ? -1 : 1))
    .map(
      (address) => `${address.street_address} ${address.city}, ${address.locality} ${address.postal_code}`,
    );

const getListingsForPersonAddresses = async (personId) => {
  const [res] = await tryCatchHandlr(
    axios.get(`/geo_addresses/listings_for_person?person_id=${personId}`, getPostJsonHeaders()),
  );

  return res?.data || {};
};

const getListings = async (blossorIds) => {
  if (!blossorIds.length) {
    return [];
  }

  const data = {
    search_request: {
      blossorIds,
      statuses: [1, 2, 3, 4, 5],
      queryOptions: {
        sort: [{ field: "listDate", order: "desc" }],
      },
    },
  };

  const [res] = await tryCatchHandlr(axios.post("/listings_api/search", data, getPostJsonHeaders()));

  return res?.data || [];
};

const mapAddress = (addressAttributes, addressListingStatuses) => ({
  addressType: addressAttributes.address_type,
  streetAddress: addressAttributes.street_address,
  cityStateZip: `${addressAttributes.city}, ${addressAttributes.locality} ${addressAttributes.postal_code}`,
  status: addressListingStatuses[addressAttributes.id]?.status || "",
  updated: addressListingStatuses[addressAttributes.id]?.updated || "",
  id: addressAttributes.id,
});

const mapAddressListingStatus = (blossorIdsForAddress, listings) => {
  if (!blossorIdsForAddress?.length) {
    return {};
  }

  let listing;
  let hasBadMatches = false;
  listings.forEach((l) => {
    // If an address has any N/A standard addresses associated with it, we don't want to trust that data for now.
    if (l.standardAddress?.startsWith("N/A,")) {
      hasBadMatches = true;
      return;
    }

    if (!listing && blossorIdsForAddress.includes(l.blossorId)) {
      listing = l;
    }
  });

  if (hasBadMatches || !listing) {
    return {};
  }

  return {
    updated: listing.lastStatusChangeDate || listing.updateDate,
    status: listing.standardStatus === "Expired" ? "Withdrawn" : listing.standardStatus,
  };
};


const DEFAULT_EDIT_ADDRESS_HANDLRS = { onSubmit: null, onDeleteHandlr: null };

const ContactInfo = () => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const editSocialMediaModalOpen = useSelector((state) => state.personDetail.editSocialMediaModalOpen);
  const messagingServiceUrl = useMessagingServiceUrl();
  const messagingApi = getExternalApi(messagingServiceUrl);
  const [dncStatuses, setDncStatuses] = useState({});
  const [conversationCounts, setConversationCounts] = useState({});
  const [addressListingStatuses, setAddressListingStatuses] = useState({});
  const [showAddAddressModal, setShowAddAddressModal] = useState(false);
  const [deleteCallBackModal, setDeleteCallBackModal] = useState(null);
  const [deleteAddressString, setDeleteAddressString] = useState("");
  const [editAddressHandlrs, setEditAddressHandlrs] = useState(DEFAULT_EDIT_ADDRESS_HANDLRS);
  const [initAddress, setInitAddress] = useState({
    addressType: "home",
    streetAddress: "",
    city: "",
    locality: "CA",
    postalCode: '',
  });

  const disabled = !useIsEditable();

  const featureFlag = useFeatureFlags();
  let addresses = [];
  if (featureFlag.areaProUser) {
    addresses = getFormattedAddressesHomeFirst(person);
    if (addresses.length > 4) {
      addresses = addresses.slice(0, 4);
    }
  }

  const getPhoneStatuses = () =>
    api
      .index([phoneNumbers(person)])
      .then(({ data }) => setDncStatuses(data))
      .catch(() => "dnc lookup failed");

  const getConversationCounts = () =>
    Promise.all(
      phoneNumbers(person).map((phoneNumber) =>
        messagingApi
          .get(
            `/Conversation/SmsAndGroupSmsCountForPersonAddressPair/${person.data?.attributes.uuid}/${phoneNumber}`,
          )
          .then((res) => ({ [phoneNumber]: res.data.total }))
          .catch(() => ({ [phoneNumber]: 0 })),
      ),
    )
      .then((res) => res.reduce((acc, val) => ({ ...acc, ...val }), {}))
      .then(setConversationCounts);

  useEffect(() => {
    getPhoneStatuses();
  }, [phoneNumbers(person).join("")]);

  useEffect(() => {
    if (!person?.data) {
      return;
    }

    const getAddresses = async () => {
      const listingsByAddressId = await getListingsForPersonAddresses(person.data.id);

      const blossorIds = Object.values(listingsByAddressId).reduce((acc, cur) => [...acc, ...cur], []);
      const listings = await getListings(blossorIds);

      const mapped = Object.entries(listingsByAddressId).reduce((acc, [addressId, blossorIdsForAddress]) => {
        acc[addressId] = mapAddressListingStatus(blossorIdsForAddress, listings);

        return acc;
      }, {});

      setAddressListingStatuses(mapped);
    };

    getAddresses();
    getConversationCounts();
  }, [person]);

  const detailsObjects = (type) =>
    person.included?.filter((a) => a.type === type && a.id !== null).map((a) => a.attributes) || [];

  const isDncPhone = (element) => {
    const phoneNumber = element.value;
    return api.isDnc(phoneNumber, dncStatuses);
  };

  const emailDetailsElement = () => {
    if (detailsObjects("email_detail")?.length > 0) {
      return sortBy(detailsObjects("email_detail"), (e) => e.position, ["desc"]).map((email, index) => (
        <EditableElementWithAdd
          // eslint-disable-next-line react/no-array-index-key
          key={`email${index}`}
          disabled={disabled}
          person={person}
          type="email"
          id={parseInt(email.id, 10)}
          updatePersonData={(data) => dispatch(updatePersonData(data))}
        />
      ));
    }

    return (
      <EditableElementWithAdd
        person={person}
        disabled={disabled}
        type="email"
        updatePersonData={(data) => dispatch(updatePersonData(data))}
      />
    );
  };

  const phoneDetailsElement = () => {
    if (detailsObjects("phone_detail").length > 0) {
      return detailsObjects("phone_detail").map((phone, index) => (
        <EditableElementWithAdd
          // eslint-disable-next-line react/no-array-index-key
          key={`phone${index}-${phone.value}`}
          disabled={disabled}
          person={person}
          type="phone"
          id={parseInt(phone.id, 10)}
          updatePersonData={(data) => dispatch(updatePersonData(data))}
          isDnc={isDncPhone(phone)}
          conversationCount={conversationCounts[phone.value]}
        />
      ));
    }
    return (
      <EditableElementWithAdd
        disabled={disabled}
        person={person}
        type="phone"
        updatePersonData={(data) => dispatch(updatePersonData(data))}
        isDnc={false}
      />
    );
  };

  const addressDetailsElement = () => {
    const addressDetails = getAddressAttributes(person).map((address) =>
      mapAddress(address, addressListingStatuses),
    );

    return (
      <div className="tw-py-[8px] tw-group" data-cy="address_pdp_card">

        <div className="tw-flex tw-justify-between tw-items-center tw-px-[20px]">

          <div className={css.brivityPersonContactLabel}>{`Address${addressDetails.length > 1 ? 'es' : ''}${addressDetails.length === 0 ? '' : ` (${addressDetails.length})`} `}</div>

          {!disabled && Boolean(addressDetails.length) && (
            <div className="tw-flex tw-gap-[4px] tw-invisible group-hover:tw-visible">
              <IconButton size="small" onClick={() => setShowAddAddressModal(true)}>
                <PlusSolidV6 />
              </IconButton>
            </div>
          )}
        </div>

        {addressDetails.length === 0 &&
          <TextButton
            schema="sentence"
            onClick={() => setShowAddAddressModal(true)}
            disabled={disabled}
            className='tw-px-20px'
          >
            Add Address
          </TextButton>
        }
        {addressDetails.map((address, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <AddressDetails key={i} address={address} setDeleteCallBackModal={setDeleteCallBackModal} setInitAddress={setInitAddress} setEditAddressHandlrs={setEditAddressHandlrs} setDeleteAddressString={setDeleteAddressString} />
        ))}
      </div>
    );
  };

  const brivityPartnersElement = (
    <div className="tw-px-[20px] tw-group hover:tw-bg-gray-5">
      <div className="tw-flex tw-justify-between tw-items-center">
        <div className={css.brivityPersonDetailsLabel}>Brivity Partners</div>
      </div>
    </div>
  );

  const socialMediaDetailsElement = () => (
    <div className="tw-py-[8px] tw-px-[20px] tw-group hover:tw-bg-gray-5">
      <div className="tw-flex tw-gap-[8px] tw-justify-between tw-items-center">
        <div className={css.brivityPersonDetailsLabel}>Social Media</div>
        {!disabled && (
          <IconButton
            size="small"
            className="tw-invisible group-hover:tw-visible"
            onClick={() => dispatch(openEditSocialMediaModal())}
          >
            <PencilSolidV6 />
          </IconButton>
        )}
      </div>
      {person.data && (
        <div className="tw-flex tw-gap-[24px] tw-w-full tw-flex-wrap">
          <SocialIcon link={person.data.attributes.facebook.value} icon="fa fa-facebook" />
          <SocialIcon link={person.data.attributes.twitter.value} icon="fab fa6-x-twitter" />
          <SocialIcon link={person.data.attributes.linkedin.value} icon="fa fa-linkedin" />
          <SocialIcon link={person.data.attributes.instagram.value} icon="fa fa-instagram" />
          <SocialIcon link={person.data.attributes.youtube.value} icon="fab fa6-youtube" />
          <SocialIcon link={person.data.attributes.tiktok.value} icon="fab fa6-tiktok" />
          <SocialIcon link={person.data.attributes.pinterest.value} icon="fab fa6-pinterest" />
        </div>
      )}
      <EditSocialMediaModal
        show={editSocialMediaModalOpen}
        onClose={() => dispatch(closeEditSocialMediaModal())}
      />
    </div>
  );

  const usedAddressTypes = person?.included?.filter(item => item.type === "address" && item.id).map(item => item.attributes.address_type)


  return (
    <CollapsibleElement title="Contact Info" id="pdp-contact-info">
      {!person.data && <Loading />}

      <AddAddressModal show={showAddAddressModal} onClose={() => setShowAddAddressModal(false)}
        usedAddressTypes={usedAddressTypes}
        onSubmit={async (payload) => {
          const editablePerson = produce(person, (draft) => {
            draft.included = [...draft.included, {
              type: "address",
              attributes: {
                address_type: payload.addressType,
                street_address: payload.streetAddress,
                city: payload.city,
                locality: payload.locality,
                postal_code: payload.postalCode,
                tempId: Date.now(),
              },
            }];
          });

          const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));


          if (err) {
            return { error: err.response?.data?.errors || 'There was an error submitting the address - double check your fields and try again, or exit out of the modal.' }
          }

          dispatch(updatePersonData(res.data));

          return { error: null };
        }} />
      <DeleteAddressModal show={Boolean(deleteCallBackModal)} onClose={() => setDeleteCallBackModal(null)} onSubmit={deleteCallBackModal} address={deleteAddressString} />
      <EditAddressModal show={Boolean(editAddressHandlrs.onSubmit)} onClose={() => setEditAddressHandlrs(DEFAULT_EDIT_ADDRESS_HANDLRS)} onSubmit={editAddressHandlrs.onSubmit} onDeleteCallBack={editAddressHandlrs.onDeleteHandlr} initAddress={initAddress} />
      <DuplicateLeadCard />
      <div id="person-contact-info-card" className="tw-mx-[-20px]">
        {emailDetailsElement()}

        {phoneDetailsElement()}

        {addressDetailsElement()}

        {featureFlag.areaProUser && (
          <>
            {brivityPartnersElement}
            <AreaPro addresses={addresses} />
          </>
        )}

        {socialMediaDetailsElement()}
      </div>
    </CollapsibleElement>
  );
};

export default ContactInfo;

const AddressDetails = ({ address, setDeleteCallBackModal, setInitAddress, setEditAddressHandlrs, setDeleteAddressString }) => {
  const statusColors = getListingStatusColors(address.status);
  const person = useSelector((state) => state.personDetail.person);
  const dispatch = useDispatch();

  const deleteCallBackModalHandler = () => {
    setDeleteAddressString(`${address.streetAddress}, ${address.cityStateZip}`);

    return setDeleteCallBackModal(() => async () => {
      const editablePerson = produce(person, (draft) => {
        const addressToUpdate = draft.included.find(
          (item) => item.type === 'address' && item.id === address.id
        );

        if (addressToUpdate) {
          addressToUpdate.attributes._destroy = true;
        }
      });
      const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));

      if (err) {
        return { error: err.response?.data?.errors || 'There was an error deleting the address - please try again.' }
      }

      dispatch(updatePersonData(res.data));
      return { error: null };
    })
  }

  return (
    <div key={`ad-${address.streetAddress}`} className="tw-py-[8px] tw-px-[20px] tw-rounded-[8px] hover:tw-bg-gray-5" data-cy="pdp_ind_addy_card">
      <div className="tw-flex tw-flex-row tw-py-[8px] tw-justify-between tw-items-center">
        <div className="tw-flex tw-gap-6px">
          <span className="tw-text-[12px] tw-leading-[24px] tw-font-semibold tw-text-gray-75 tw-capitalize tw-rounded-[6px] tw-border-solid tw-border-1px tw-border-gray-15 tw-px-8px tw-flex tw-items-center">
            {address.addressType}
          </span>
          {address.status ? (
            <span
              className={`tw-rounded-lg tw-px-8px tw-py-4px tw-text-12px tw-font-semibold ${statusColors.background} ${statusColors.text}`}
            >
              {address.status}
            </span>
          ) : null}
        </div>
        <div className="tw-relative">
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton schema="transparent" size="small">
                <EllipsisSolidV6 size='m' className='tw-text-gray-75' />
              </IconButton>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverItem onClick={() => {
                setInitAddress({
                  addressType: address.addressType,
                  streetAddress: address.streetAddress,
                  city: address.cityStateZip.split(',')[0],
                  locality: address.cityStateZip.split(' ')[1],
                  postalCode: address.cityStateZip.split(' ')[2],
                });

                setEditAddressHandlrs({
                  onSubmit: async (ad) => {
                    const editablePerson = produce(person, (draft) => {
                      const addressToUpdate = draft.included.find(
                        (item) => item.type === 'address' && item.id === address.id
                      );

                      if (addressToUpdate) {
                        addressToUpdate.attributes = {
                          ...addressToUpdate.attributes,
                          ...snakefyKeys(ad),
                        }
                      }
                    });

                    const [res, err] = await tryCatchHandlr(updatePersonPromise(editablePerson));
                    if (err) {
                      return { error: err.response?.data?.errors || 'There was an error submitting the address - double check your fields and try again, or exit out of the modal.' }
                    }
                    dispatch(updatePersonData(res.data));
                    return { error: null };
                  },
                  onDeleteHandlr: deleteCallBackModalHandler,
                })
              }} icon={<PencilSolidV6 />}>
                <span className={STYLES.ellipsisTxt}>Edit Address</span>
              </PopoverItem>
              <PopoverItem onClick={() => {
                deleteCallBackModalHandler();
              }
              } icon={<TrashCanSolidV6 />}>
                <span className={STYLES.ellipsisTxt}>Delete Address</span>
              </PopoverItem>
            </PopoverContent>
          </Popover>
        </div>
      </div >
      <div className="tw-flex tw-flex-col tw-text-14px tw-text-nowrap tw-mb-4px">
        <span>{address.streetAddress}</span>
        <span>{address.cityStateZip}</span>
      </div>
      {
        address.updated ? (
          <span className="tw-text-gray-50 tw-text-12d">
            System notified of status change{" "}
            {new Date(address.updated).toLocaleDateString("en-US", { timeZone: "UTC" })}
          </span>
        ) : null
      }
    </div >)

};

AddressDetails.propTypes = {
  address: PropTypes.shape({
    addressType: PropTypes.string,
    streetAddress: PropTypes.string,
    cityStateZip: PropTypes.string,
    status: PropTypes.string,
    updated: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
  setDeleteCallBackModal: PropTypes.func.isRequired,
  setInitAddress: PropTypes.func.isRequired,
  setEditAddressHandlrs: PropTypes.func.isRequired,
  setDeleteAddressString: PropTypes.func.isRequired,
};
